import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MemberInCourseDTO } from "../../model/entity/MemberInCourseDTO";
import { Member } from "../../model/entity/Member";
import {
  SelectChangeEvent,
  Container,
  Paper,
  Typography,
  Grid2,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import handleAxiosError from "../../components/ErrorHandler";
import { notifOk, notifErr } from "../../components/Functions";
import { Gender } from "../../model/common/Gender";
import {
  memberValidationObjInit,
  validateMember,
} from "../../model/validation/MemberValidation";
import { Loading } from "../../components/Loading";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { ThumbUp, ThumbDown, Forward } from "@mui/icons-material";
import { MemberFee } from "../../model/entity/MemberFee";
import {
  deleteFetcher,
  getFetcher,
  putFetcher,
} from "../../components/Fetchers";

const EditMember = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [member, setMember] = useState<Member | null>(null);
  const [memberErr, setMemberErr] = useState(memberValidationObjInit);
  const [memberFee, setMemberFee] = useState<MemberFee | null>(null);
  const [memberInCourseDTOs, setMemberInCoursesDTOs] = useState<
    MemberInCourseDTO[] | null
  >(null);

  useEffect(() => {
    const fetchData = async (url: string, callbackfn: any) => {
      try {
        const response = await getFetcher(url);
        callbackfn(response.data);
      } catch (err) {
        notifErr(handleAxiosError(err));
      }
    };

    fetchData(`${process.env.REACT_APP_API_URL}/members/${id}`, setMember);
    fetchData(
      `${process.env.REACT_APP_API_URL}/member-fees/active-by-member/${id}`,
      setMemberFee
    );
    fetchData(
      `${process.env.REACT_APP_API_URL}/member-in-courses/active-by-member/${id}`,
      (data: MemberInCourseDTO[]) => {
        const sortedData = data.sort(sortMemberInCourses);
        setMemberInCoursesDTOs(sortedData);
      }
    );
  }, [id]);

  const sortMemberInCourses = (a: MemberInCourseDTO, b: MemberInCourseDTO) => {
    return a.courseDTO!.name.localeCompare(b.courseDTO!.name);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!member) return;
    setMember({ ...member, [e.target.name]: e.target.value });
  };

  const handleGenderInputChange = (e: SelectChangeEvent) => {
    if (!member) return;
    setMember({ ...member, [e.target.name]: e.target.value });
  };

  const handleMemberFeeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!memberFee) return;
    setMemberFee({ ...memberFee, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if (!member) return;
    if (triedSubmit) setMemberErr(validateMember(member));
  }, [member, triedSubmit]);

  const handleMemberPaidChange = (index: number) => {
    if (!memberInCourseDTOs) return;
    const currentMemberInCourseDTO = memberInCourseDTOs.at(index);
    if (!currentMemberInCourseDTO) return;

    const updatedMemberInCourseDTO: MemberInCourseDTO = {
      ...currentMemberInCourseDTO,
      paid: !currentMemberInCourseDTO.paid,
    };

    const updatedMemberInCourseDTOs: MemberInCourseDTO[] =
      memberInCourseDTOs.map((mic, i) =>
        i === index ? updatedMemberInCourseDTO : mic
      );

    setMemberInCoursesDTOs(updatedMemberInCourseDTOs);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!member || !memberFee) return;
    setTriedSubmit(true);

    const memberErrors = validateMember(member);
    setMemberErr(memberErrors);

    if (Object.values(memberErrors).some((val) => val === false)) return;

    try {
      await putFetcher(
        `${process.env.REACT_APP_API_URL}/members/${member.id}`,
        member
      );
      await putFetcher(
        `${process.env.REACT_APP_API_URL}/member-fees/${memberFee.id}`,
        memberFee
      );
      await putFetcher(
        `${process.env.REACT_APP_API_URL}/member-in-courses/bulk`,
        memberInCourseDTOs
      );
      notifOk("Změny byly uloženy.");
      navigate(-1);
    } catch (err) {
      notifErr(handleAxiosError(err));
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleDeleteConfirm = async () => {
    if (!member) return;
    try {
      await deleteFetcher(
        `${process.env.REACT_APP_API_URL}/members/${member.id}`
      );
      notifOk("Člen úspěšně smazán.");
      navigate(-1);
    } catch (err) {
      notifErr(handleAxiosError(err));
    } finally {
      setOpenDialog(false);
    }
  };

  if (member === null || memberInCourseDTOs === null || memberFee === null)
    return <Loading />;

  const dialogMsg = (
    <div>
      Opravdu chcete odebrat člena
      <b> {member.firstName + " " + member.lastName}</b>?<br />
      Tato akce je nevratná.
    </div>
  );

  return (
    <Container
      maxWidth="md"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pb: 2,
      }}
    >
      <ConfirmationDialog
        open={openDialog}
        setOpen={setOpenDialog}
        msg={dialogMsg}
        handleSubmit={handleDeleteConfirm}
      />
      <Paper elevation={3} sx={{ padding: "1.5rem" }}>
        <Typography variant="h5" sx={{ marginBottom: "1.5rem" }}>
          {member.firstName + " " + member.lastName}
        </Typography>
        <Grid2 container spacing={3} size={{ xs: 12, md: 6 }}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Jméno"
              required
              name="firstName"
              value={member.firstName}
              error={!memberErr.firstName}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Příjmení"
              required
              name="lastName"
              value={member.lastName}
              error={!memberErr.lastName}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD.MM.YYYY"
                label="Datum narození"
                sx={{ width: "100%" }}
                name="birthDate"
                value={member.birthDate === "" ? null : dayjs(member.birthDate)}
                onChange={(date) =>
                  setMember({
                    ...member,
                    birthDate: date ? date.format("YYYY-MM-DD") : "",
                  })
                }
                slotProps={{
                  textField: {
                    size: "small",
                    required: true,
                    error:
                      !memberErr.birthDate &&
                      !dayjs(member.birthDate, "YYYY-MM-DD", true).isValid(),
                  },
                  field: { clearable: true },
                }}
              />
            </LocalizationProvider>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormControl required size="small" fullWidth>
              <InputLabel>Pohlaví</InputLabel>
              <Select
                label="Pohlaví"
                name="gender"
                value={member.gender}
                error={!memberErr.gender}
                onChange={handleGenderInputChange}
              >
                <MenuItem value={Gender.MALE}>Muž</MenuItem>
                <MenuItem value={Gender.FEMALE}>Žena</MenuItem>
              </Select>
            </FormControl>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Email"
              type="email"
              required
              name="email"
              value={member.email}
              error={!memberErr.email}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Telefon"
              type="tel"
              required
              name="phone"
              value={member.phone}
              error={!memberErr.phone}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Adresa bydliště"
              required
              name="address"
              value={member.address}
              error={!memberErr.address}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Rodné číslo (bez lomítka)"
              name="nationalId"
              value={member.nationalId}
              error={!memberErr.nationalId}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              label="Poznámka"
              multiline
              rows={4}
              name="note"
              value={member.note ?? ""}
              error={!memberErr.note}
              onChange={handleInputChange}
            />
          </Grid2>
          <Grid2
            size={{ xs: 12, md: 6 }}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexDirection: "column",
            }}
          >
            <TextField
              fullWidth
              size="small"
              label="Vypočítané příspěvky (Kč)"
              value={memberFee.calculatedFee}
              slotProps={{
                input: {
                  readOnly: true,
                },
              }}
            />
            <TextField
              fullWidth
              size="small"
              label="Reálné příspěvky (Kč)"
              type="number"
              name="actualFee"
              value={memberFee.actualFee}
              onChange={(e) => handleMemberFeeChange(e)}
            />
          </Grid2>

          {/* START SIGNED UP COURSES */}

          <Grid2
            size={{ xs: 12 }}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <Typography fontWeight="bold" variant="subtitle1">
              Přihlášené kurzy
            </Typography>
          </Grid2>

          <Grid2
            size={{ xs: 12 }}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <Table
              size="small"
              sx={{
                width: "60%",
                maxHeight: "14.5rem",
                overflowY: "auto",
                scrollbarWidth: "thin",
                display: "block",
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    position: "sticky",
                    top: 0,
                    background: "white",
                    zIndex: 100,
                  }}
                >
                  <TableCell sx={{ fontWeight: "bold", width: "35%" }}>
                    Název
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "15%" }}>
                    Příspěvky
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>
                    Zaplaceno
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: "bold", textAlign: "right", width: "5%" }}
                  >
                    Přejít
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {memberInCourseDTOs.map(
                  (mic, index) =>
                    mic.courseDTO !== undefined && (
                      <TableRow key={index}>
                        <TableCell>{mic.courseDTO.name}</TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                          {mic.courseDTO.fee}Kč
                        </TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMemberPaidChange(index)}
                          >
                            {mic.paid ? (
                              <ThumbUp className="thumbUp" />
                            ) : (
                              <ThumbDown className="thumbDown" />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ textAlign: "right" }}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              mic.courseDTO !== undefined &&
                              navigate(
                                `/overview/courses/edit/${mic.courseDTO.id}`
                              )
                            }
                          >
                            <Forward sx={{ color: "var(--gradientStart)" }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    )
                )}
              </TableBody>
            </Table>
          </Grid2>

          {/* END SIGNED UP COURSES */}

          <Grid2
            container
            size={{ xs: 12 }}
            sx={{ justifyContent: "space-between" }}
          >
            <Button
              variant="contained"
              className="deleteBtn"
              onClick={() => setOpenDialog(true)}
            >
              Smazat
            </Button>
            <div>
              <Button
                variant="outlined"
                className="cancelBtn"
                onClick={handleCancel}
                sx={{ mr: 1.5 }}
              >
                Zrušit
              </Button>
              <Button
                variant="outlined"
                className="submitBtn"
                onClick={handleSubmit}
              >
                Uložit
              </Button>
            </div>
          </Grid2>
        </Grid2>
      </Paper>
    </Container>
  );
};

export default EditMember;
